/**
 * AP-37 — **Kalkulation** (Modul `CALCULATION`): der Vertrag zwischen Backend,
 * Frontend und den Nachbarn.
 *
 * Hier stehen die Werte, die beide Seiten gleich kennen müssen — Status und
 * erlaubte Übergänge, Kostenarten, Positionsarten — und der Vertrag zur
 * Nachkalkulation (`CalculationSummary`, AP-37 6.3). Gerechnet wird nicht hier,
 * sondern in `@symphenia/calc-core` (AP-37 4.7).
 */

// ─── Lebenslauf (AP-37 4.1) ──────────────────────────────────────────────────

export const CALCULATION_STATUSES = ['IN_PROGRESS', 'SUBMITTED', 'WON', 'LOST', 'DISCARDED'] as const
export type CalculationStatus = (typeof CALCULATION_STATUSES)[number]

/**
 * Erlaubte Wechsel über den Status-Endpunkt (Muster `PROJECT_STATUS_TRANSITIONS`).
 *
 * Zurück nach `IN_PROGRESS` geht es **nicht** über den Status, sondern nur über
 * eine neue Fassung (AP-37 4.1, 4.16) — die alte bleibt, wie sie war. `WON` ist
 * endgültig; Änderungen laufen im Projekt als Nachtrag.
 */
export const CALCULATION_STATUS_TRANSITIONS: Readonly<Record<CalculationStatus, readonly CalculationStatus[]>> = {
  IN_PROGRESS: ['SUBMITTED', 'DISCARDED'],
  SUBMITTED: ['WON', 'LOST', 'DISCARDED'],
  WON: [],
  LOST: [],
  DISCARDED: [],
}

export function calculationStatusAllowed(von: CalculationStatus, nach: CalculationStatus): boolean {
  return CALCULATION_STATUS_TRANSITIONS[von]?.includes(nach) ?? false
}

/** Aus diesen Status führt „neue Fassung" zurück nach `IN_PROGRESS` (4.1). */
export const CALCULATION_REVISABLE_STATUSES: readonly CalculationStatus[] = ['SUBMITTED', 'LOST', 'DISCARDED']

// ─── Rechnen (AP-37 4.3–4.7) ─────────────────────────────────────────────────

/** Lohnmodus: Kostensatz + Aufschläge oder Verrechnungssatz je Qualifikation. */
export const CALCULATION_LABOR_MODES = ['COST_PLUS', 'BILLING_RATE'] as const
export type CalculationLaborMode = (typeof CALCULATION_LABOR_MODES)[number]

/** Kostenarten. `LABOR` gibt es nur in „gilt für" eines Aufschlags und im Ergebnis. */
export const CALCULATION_COST_KINDS = ['LABOR', 'MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER'] as const
export type CalculationCostKind = (typeof CALCULATION_COST_KINDS)[number]

/** Die Kostenarten einer Kostenzeile (ohne Lohn — der kommt aus den Arbeitsschritten). */
export const CALCULATION_LINE_COST_KINDS = ['MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER'] as const
export type CalculationLineCostKind = (typeof CALCULATION_LINE_COST_KINDS)[number]

export const CALCULATION_POSITION_KINDS = ['NORMAL', 'OPTIONAL', 'ALTERNATIVE', 'REGIE', 'SURCHARGE', 'TEXT'] as const
export type CalculationPositionKind = (typeof CALCULATION_POSITION_KINDS)[number]

export const CALCULATION_QUANTITY_BASES = ['PER_UNIT', 'TOTAL'] as const
export type CalculationQuantityBasis = (typeof CALCULATION_QUANTITY_BASES)[number]

export const CALCULATION_SURCHARGE_BASES = ['DIRECT', 'RUNNING'] as const
export type CalculationSurchargeBasis = (typeof CALCULATION_SURCHARGE_BASES)[number]

/** Abgeleiteter Zustand einer Position (4.2). */
export const CALCULATION_POSITION_STATES = ['OPEN', 'PRICED', 'MANUAL', 'GAP_OPEN'] as const
export type CalculationPositionState = (typeof CALCULATION_POSITION_STATES)[number]

export const CALCULATION_LINE_ORIGINS = ['MANUAL', 'TEMPLATE', 'QUICK', 'AI'] as const
export type CalculationLineOrigin = (typeof CALCULATION_LINE_ORIGINS)[number]

export const CALCULATION_PRICE_SOURCES = ['ARTICLE', 'MANUAL', 'OFFER'] as const
export type CalculationPriceSource = (typeof CALCULATION_PRICE_SOURCES)[number]

/** Dateiart im Kalkulationsverzeichnis (4.18.1). */
export const CALCULATION_FILE_KINDS = ['SUPPLIER_OFFER', 'MATERIAL_LIST', 'SPECIFICATION', 'DRAWING', 'OTHER'] as const
export type CalculationFileKind = (typeof CALCULATION_FILE_KINDS)[number]

/** Rundungsschritte des Einheitspreises (4.1). */
export const CALCULATION_ROUNDING_STEPS = [0.01, 0.05, 0.1, 1] as const

// ─── Einstellungen (AP-37 10.) ───────────────────────────────────────────────

/** Vorgabe der Kalkulationsnummer: `K0142-26` (4.1). */
export const CALCULATION_NUMBER_PATTERN_DEFAULT = 'K{nummer}-{jahr2}'

/** Die Reiter der Einstellungsseite, in Anzeigereihenfolge. */
export const CALCULATION_SETTING_TABS = [
  'allgemein',
  'nummernkreis',
  'schemata',
  'saetze',
  'phasen',
  'verschnitt',
  'gruende',
  'lv',
] as const
export type CalculationSettingTab = (typeof CALCULATION_SETTING_TABS)[number]

/** Anzeige einer Kalkulationsnummer mit Fassung: `K0142-26 · F2` (4.1). */
export function calculationLabel(number: string, revision: number): string {
  return revision > 1 ? `${number} · F${revision}` : number
}

// ─── Nachkalkulation (AP-37 6.3) ─────────────────────────────────────────────

/**
 * Eine Zeile der Spalte „aus Kalkulation" in der Nachkalkulation des Projekts.
 * Das Projekt ordnet über die Verkaufszeilen zu (`sourceRef` → Kalkulationsposition)
 * und rechnet **anteilig nach Menge**.
 */
export interface CalculationSummaryLine {
  calculationPositionId: string
  /** kalkulierte Menge */
  quantity: number
  /** null = ohne Phase */
  phaseKey: string | null
  hours: number
  /** mit Kostensatz, ohne Aufschläge */
  laborCost: number
  /** Einzelkosten MATERIAL */
  materialCost: number
  /** SUBCONTRACT + EQUIPMENT + OTHER */
  otherCost: number
  /** GP, nach Nachlass anteilig */
  salesPrice: number
}

export interface CalculationSummary {
  calculationId: string
  revision: number
  lines: CalculationSummaryLine[]
}

/** Was das Projektmodul über eine verknüpfte Kalkulation lesen darf (Lookup, 8.). */
export interface CalculationLookupEntry {
  id: string
  number: string
  revision: number
  name: string
}
